import type { HackcalDay, HackcalEvent, HackcalResponse } from "./types";
import { linkify } from "./linkify";
import { Calendar } from "./calendar";
import {
  buildGoogleCalendarUrl,
  buildIcsCalendar,
  DEFAULT_DURATION_MINUTES,
  downloadIcsFile,
  parseStart,
  toBerlinIsoString,
  type CalendarEventInput,
} from "./ics";

const HACKCAL_URL = "https://hackcal.aalen.space/";

/**
 * Window in which repeat callers share one response. The homepage runs two
 * independent pollers over this endpoint (the next-event teaser and the
 * countdown) on the same cadence, which meant two identical requests every
 * cycle; this collapses them to one while staying well short of any interval.
 */
const FETCH_DEDUPE_MS = 5_000;

let cachedResponse: { at: number; data: HackcalResponse } | null = null;
let inFlight: Promise<HackcalResponse> | null = null;

/** Drops the shared response cache (teardown / tests). */
export function resetHackcalCache(): void {
  cachedResponse = null;
  inFlight = null;
}

export interface Sondertermin {
  /** YYYY-MM-DD */
  date: string;
  /** HH:MM */
  time: string;
  title: string;
  location?: string;
  description?: string;
}

const SONDERTERMINE_ELEMENT_ID = "cms-sondertermine";

/** Reads the CMS-authored Sondertermine list vite.config.ts embeds as JSON in termine.html. */
function readSondertermine(): Sondertermin[] {
  const text = document.getElementById(SONDERTERMINE_ELEMENT_ID)?.textContent;
  if (!text) return [];
  try {
    const parsed: unknown = JSON.parse(text);
    return Array.isArray(parsed) ? (parsed as Sondertermin[]) : [];
  } catch {
    return [];
  }
}

const WEEKDAY_FORMAT = new Intl.DateTimeFormat("de-DE", { weekday: "long" });
const DAY_NAME_FORMAT = new Intl.DateTimeFormat("de-DE", { day: "numeric", month: "long", year: "numeric" });

function capitalize(s: string): string {
  return s.length > 0 ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/**
 * Merges CMS-authored Sondertermine into the live hackcal response, in the same {dateKey:
 * HackcalDay} shape every other function in this file already expects - so the calendar widget,
 * "next event" teaser, countdown, .ics export, and structured data all pick them up for free,
 * with zero extra wiring. Purely additive and read-only towards hackcal: never mutates `data` or
 * anything nested in it (a day untouched by a Sondertermin is returned by reference, unchanged).
 *
 * `render()` (below) and findNextEvent() assume chronological key order, so days affected by a
 * merge are rebuilt and the whole result is re-sorted by dateKey - safe because "YYYY-MM-DD"
 * sorts lexicographically the same as chronologically.
 */
export function mergeSondertermine(data: HackcalResponse, sondertermine: Sondertermin[]): HackcalResponse {
  if (sondertermine.length === 0) return data;

  const merged = new Map<string, HackcalDay>(Object.entries(data));

  sondertermine.forEach((sondertermin, index) => {
    const parsedDate = new Date(`${sondertermin.date}T00:00:00`);
    if (Number.isNaN(parsedDate.getTime())) return; // malformed entry - skip rather than crash

    const existingDay = merged.get(sondertermin.date);
    // A day with existing hackcal events keeps its own name/weekday (hackcal is the source of
    // truth there); only a brand-new day needs one computed from the date.
    const day: HackcalDay = existingDay
      ? { ...existingDay, events: { ...existingDay.events } }
      : {
          name: capitalize(DAY_NAME_FORMAT.format(parsedDate)),
          weekday: capitalize(WEEKDAY_FORMAT.format(parsedDate)),
          events: {},
        };

    // Sondertermine are appended after any existing hackcal events for that day rather than
    // time-sorted together - both same-day hackcal events and same-day Sondertermine are rare
    // enough that this simplification isn't worth the extra complexity.
    day.events[`sondertermin-${index}`] = {
      datestr: sondertermin.time,
      summary: sondertermin.title,
      location: sondertermin.location || undefined,
      description: sondertermin.description || undefined,
      source: "sondertermin",
    };

    merged.set(sondertermin.date, day);
  });

  return Object.fromEntries([...merged.entries()].sort(([a], [b]) => a.localeCompare(b)));
}

export async function fetchHackcal(): Promise<HackcalResponse> {
  if (cachedResponse && Date.now() - cachedResponse.at < FETCH_DEDUPE_MS) {
    return cachedResponse.data;
  }
  // A request already on the wire serves every caller that arrives meanwhile.
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const res = await fetch(HACKCAL_URL, { cache: "no-store" });
      if (!res.ok) throw new Error(`hackcal responded with ${res.status}`);
      const raw = (await res.json()) as HackcalResponse;
      const data = mergeSondertermine(raw, readSondertermine());
      // Only successful reads are cached, so a failure never gets replayed.
      cachedResponse = { at: Date.now(), data };
      return data;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

export interface NextEvent {
  dateKey: string;
  dayName: string;
  weekday: string;
  datestr: string;
  summary: string;
}

/** Assumes hackcal returns days/events in chronological order (as observed from the API). */
export function findNextEvent(data: HackcalResponse): NextEvent | null {
  const firstDate = Object.keys(data)[0];
  if (!firstDate) return null;

  const day = data[firstDate];
  const firstUid = Object.keys(day.events)[0];
  if (!firstUid) return null;

  const event = day.events[firstUid];
  return {
    dateKey: firstDate,
    dayName: day.name,
    weekday: day.weekday,
    datestr: event.datestr,
    summary: event.summary,
  };
}

function buildAddToCalendarControls(dateKey: string, uid: string, event: HackcalEvent): HTMLElement | null {
  const googleUrl = buildGoogleCalendarUrl({ uid, dateKey, event });
  if (!googleUrl) return null;

  const wrap = document.createElement("div");
  wrap.className = "event-calendar-actions";

  const googleLink = document.createElement("a");
  googleLink.className = "event-calendar-btn";
  googleLink.href = googleUrl;
  googleLink.target = "_blank";
  googleLink.rel = "noopener";
  googleLink.textContent = "+ Google Kalender";
  wrap.append(googleLink);

  const icsBtn = document.createElement("button");
  icsBtn.type = "button";
  icsBtn.className = "event-calendar-btn";
  icsBtn.textContent = "+ .ics-Datei";
  icsBtn.addEventListener("click", () => {
    const content = buildIcsCalendar([{ uid, dateKey, event }]);
    downloadIcsFile(`hackwerk-${dateKey}.ics`, content);
  });
  wrap.append(icsBtn);

  return wrap;
}

function buildEventCard(dateKey: string, uid: string, event: HackcalEvent): HTMLElement {
  const { datestr, summary, location, description } = event;
  const card = document.createElement("article");
  card.className = "event-card";

  const time = document.createElement("span");
  time.className = "event-time";
  time.textContent = datestr;

  const title = document.createElement("h2");
  title.className = "event-summary";
  title.textContent = summary;

  card.append(time, title);

  if (event.source === "sondertermin") {
    const badge = document.createElement("span");
    badge.className = "event-badge";
    badge.textContent = "Sondertermin";
    card.append(badge);
  }

  if (location) {
    const loc = document.createElement("p");
    loc.className = "event-details";
    loc.append("📍 ", linkify(location));
    card.append(loc);
  }

  if (description) {
    const desc = document.createElement("p");
    desc.className = "event-details";
    desc.append(linkify(description));
    card.append(desc);
  }

  const calendarControls = buildAddToCalendarControls(dateKey, uid, event);
  if (calendarControls) card.append(calendarControls);

  return card;
}

function buildDaySection(dateKey: string, dayName: string, weekday: string): HTMLElement {
  const heading = document.createElement("div");
  heading.className = "event-day";
  heading.id = `day-${dateKey}`;
  const name = document.createElement("span");
  name.className = "event-day-name";
  name.textContent = dayName;
  const wd = document.createElement("span");
  wd.className = "event-day-weekday";
  wd.textContent = weekday;
  heading.append(name, wd);
  return heading;
}

function buildDownloadAllButton(data: HackcalResponse): HTMLElement | null {
  const events: CalendarEventInput[] = Object.entries(data).flatMap(([dateKey, day]) =>
    Object.entries(day.events).map(([uid, event]) => ({ uid, dateKey, event })),
  );
  if (events.length === 0) return null;

  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "event-calendar-btn event-calendar-btn-all";
  btn.textContent = "Alle Termine als .ics herunterladen";
  btn.addEventListener("click", () => {
    downloadIcsFile("hackwerk-termine.ics", buildIcsCalendar(events));
  });
  return btn;
}

let calendarInstance: Calendar | null = null;

function scrollToDay(dateKey: string): void {
  const target = document.getElementById(`day-${dateKey}`);
  if (!target) return;

  const header = document.querySelector<HTMLElement>(".site-header");
  const headerHeight = header?.getBoundingClientRect().height ?? 0;
  const rect = target.getBoundingClientRect();
  window.scrollBy({ top: rect.top - headerHeight - 16, behavior: "smooth" });

  target.classList.add("is-highlighted");
  window.setTimeout(() => target.classList.remove("is-highlighted"), 1600);
}

function renderCalendarWidget(data: HackcalResponse): void {
  const calendarContainer = document.getElementById("calendarWidget");
  if (!calendarContainer) return;

  if (calendarInstance) {
    calendarInstance.updateData(data);
  } else {
    calendarInstance = new Calendar(calendarContainer, data, { onSelectDay: scrollToDay });
  }
}

const EVENT_STRUCTURED_DATA_ID = "event-structured-data";

/** Google Event rich results need a per-event Place/Organization/startDate — built fresh from the live hackcal data. */
function buildEventStructuredData(data: HackcalResponse): Record<string, unknown>[] {
  return Object.entries(data).flatMap(([dateKey, day]) =>
    Object.values(day.events).flatMap((event) => {
      const start = parseStart(dateKey, event.datestr);
      if (!start) return [];
      const end = new Date(start.getTime() + DEFAULT_DURATION_MINUTES * 60_000);

      return [
        {
          "@context": "https://schema.org",
          "@type": "Event",
          name: event.summary,
          startDate: toBerlinIsoString(start),
          endDate: toBerlinIsoString(end),
          eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
          eventStatus: "https://schema.org/EventScheduled",
          isAccessibleForFree: true,
          description: event.description ?? event.summary,
          // Individual hackcal entries have no photo of their own; Google's Event structured
          // data guide lists "image" as a recommended (not required) property, and the
          // termine.html social-preview image is a reasonable stand-in for every entry.
          image: "https://aalen.space/og-termine.png",
          url: `https://aalen.space/termine.html#day-${dateKey}`,
          location: {
            "@type": "Place",
            name: event.location ?? "AAccelerator",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Blezingerstraße 15",
              postalCode: "73430",
              addressLocality: "Aalen",
              addressCountry: "DE",
            },
          },
          organizer: {
            "@type": "Organization",
            name: "Hackwerk e.V.",
            url: "https://aalen.space/",
          },
        },
      ];
    }),
  );
}

function renderEventStructuredData(data: HackcalResponse): void {
  const events = buildEventStructuredData(data);
  const existing = document.getElementById(EVENT_STRUCTURED_DATA_ID);

  if (events.length === 0) {
    existing?.remove();
    return;
  }

  const script = existing instanceof HTMLScriptElement ? existing : document.createElement("script");
  script.id = EVENT_STRUCTURED_DATA_ID;
  script.type = "application/ld+json";
  script.textContent = JSON.stringify(events.length === 1 ? events[0] : events);
  if (!existing) document.head.append(script);
}

function render(container: HTMLElement, data: HackcalResponse): void {
  container.dataset.state = "ready";
  container.replaceChildren();
  renderCalendarWidget(data);
  renderEventStructuredData(data);

  const dates = Object.keys(data);
  if (dates.length === 0) {
    const empty = document.createElement("p");
    empty.className = "event-status enter-rise";
    empty.textContent = "Aktuell sind keine Termine geplant.";
    container.append(empty);
    return;
  }

  const downloadAllBtn = buildDownloadAllButton(data);
  if (downloadAllBtn) container.append(downloadAllBtn);

  for (const dateKey of dates) {
    const day = data[dateKey];
    container.append(buildDaySection(dateKey, day.name, day.weekday));

    for (const uid of Object.keys(day.events)) {
      container.append(buildEventCard(dateKey, uid, day.events[uid]));
    }
  }

  staggerEntrance(container);
}

const ENTRANCE_STEP_MS = 55;
const ENTRANCE_MAX_STEPS = 8;

/** Cascades the freshly inserted rows in, instead of the whole list popping at once. */
function staggerEntrance(container: HTMLElement): void {
  Array.from(container.children).forEach((child, index) => {
    const el = child as HTMLElement;
    el.classList.add("enter-rise");
    const step = Math.min(index, ENTRANCE_MAX_STEPS);
    if (step > 0) el.style.animationDelay = `${step * ENTRANCE_STEP_MS}ms`;
  });
}

function renderError(container: HTMLElement): void {
  container.dataset.state = "error";
  container.replaceChildren();
  document.getElementById(EVENT_STRUCTURED_DATA_ID)?.remove();
  const error = document.createElement("p");
  error.className = "event-status enter-rise";
  error.textContent = "Termine konnten nicht geladen werden.";
  container.append(error);
}

export async function initEvents(): Promise<void> {
  const container = document.getElementById("eventList");
  if (!container) return;

  try {
    const data = await fetchHackcal();
    render(container, data);
  } catch (err) {
    console.error("Konnte Termine nicht laden", err);
    renderError(container);
  }
}
